import { createAction, props } from "@ngrx/store";
import { ToastMessage } from "../../models/common.model";
import { VigilantApiDtosUsersSelfGetResponse } from "../../api/vigilant/users/users.model";

const prefix = "App.";

/**
 * Action to set the user's authentication state.
 * @param userLoggedIn - Flag indicating whether the user is currently logged in.
 * @param userAuthData - Additional authentication data associated with the user.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SetAuthState = createAction(`${prefix}SetAuthState`, props<{ userLoggedIn: boolean; userAuthData: any }>());

/**
 * Action to update the user information in the store.
 * @param userSuperAdmin - Indicates if the user has super admin privileges.
 * @param userId - Unique identifier for the user.
 * @param userEmail - Email address of the user.
 * @param userFirstName - The user's first name.
 * @param userLastName - The user's last name.
 * @param userRootOrgId - Identifier for the user's root Channel; can be null or undefined if not applicable.
 * @param userSelf - The user's full VigilantApiDtosUsersSelfGetResponse object.
 */
export const SetUserInfo = createAction(
  `${prefix}SetUserInfo`,
  props<{
    userSuperAdmin: boolean;
    userId: number;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
    userRootOrgId: number | null | undefined;
    userSelf: VigilantApiDtosUsersSelfGetResponse | undefined;
  }>()
);

/**
 * Action to update the API version in the state.
 * @param apiVersion - The current version of the API.
 */
export const SetAPIVersion = createAction(`${prefix}SetAPIVersion`, props<{ apiVersion: string }>());

/**
 * Action to open a dialog with the specified title and message.
 * @param title - The title of the dialog.
 * @param message - The message to display in the dialog.
 * @param modal - Flag indicating whether the dialog should be modal.
 */
export const OpenDialog = createAction(`${prefix}OpenDialog`, props<{ title: string; message: string; modal: boolean }>());

/**
 * Action to close any open dialog.
 */
export const CloseDialog = createAction(`${prefix}CloseDialog`);

/**
 * Action to add toast notifications to the state.
 * @param toasts - An array of toast messages to be displayed.
 */
export const AddToasts = createAction(`${prefix}AddToasts`, props<{ toasts: ToastMessage[] }>());

/**
 * Action to clear all toast notifications from the state.
 */
export const ClearToasts = createAction(`${prefix}ClearToasts`);

/**
 * Action to display an overlay.
 */
export const ShowOverlay = createAction(`${prefix}ShowOverlay`);

/**
 * Action to hide the overlay.
 */
export const HideOverlay = createAction(`${prefix}HideOverlay`);

/**
 * Action to display a loading spinner.
 */
export const ShowSpinner = createAction(`${prefix}ShowSpinner`);

/**
 * Action to hide the loading spinner.
 */
export const HideSpinner = createAction(`${prefix}HideSpinner`);

/**
 * Action to block the user interface, optionally displaying a spinner.
 * @param spinner - Optional flag indicating whether to display a spinner while blocking the UI.
 */
export const ShowBlockUI = createAction(`${prefix}ShowBlockUI`, props<{ spinner?: boolean }>());

/**
 * Action to unblock the user interface, optionally hiding the spinner.
 * @param spinner - Optional flag indicating whether to hide the spinner while unblocking the UI.
 */
export const HideBlockUI = createAction(`${prefix}HideBlockUI`, props<{ spinner?: boolean }>());

/**
 * Action to add a breadcrumb to the navigation trail.
 * @param breadcrumb - The breadcrumb string to be added.
 */
export const PushBreadcrumb = createAction(`${prefix}PushBreadcrumb`, props<{ breadcrumb: string }>());

/**
 * Action to remove the most recent breadcrumb from the navigation trail.
 */
export const PopBreadcrumb = createAction(`${prefix}PopBreadcrumb`);

/**
 * Action to set the root Channel option in the state.
 * @param rootOrgOption - The selected root Channel option; may be undefined if not set.
 */
export const SetRootOrgOption = createAction(`${prefix}SetRootOrgOption`, props<{ rootOrgOption: number | undefined }>());
